import React, {
  useMemo,
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";
import {
  MdSpaceDashboard,
  MdGroups,
  MdLocalHospital,
  MdPersonPin,
  MdLocationOn,
  MdKey,
  MdVerifiedUser,
  MdManageAccounts,
  MdAdminPanelSettings,
  MdCreditCard,
  MdFactCheck,
  MdAutoAwesome,
  MdInsights,
  MdBolt,
  MdWbSunny,
  MdWbTwilight,
  MdDarkMode,
  MdArrowUpward,
  MdArrowDownward,
  MdTrendingFlat,
  MdArrowForward,
  MdRefresh,
  MdErrorOutline,
  MdMemory,
  MdQueryStats,
} from "react-icons/md";
import { useAuth } from "../../Context/AuthContext";
import { useChartTheme } from "../../Context/ThemeContext";
import {
  PermissionCodes,
  permissionHas,
  resolveUserPermissionCodes,
  isSuperAdminUser,
} from "../../Utils/Permissions";
import PageTitle from "../../Components/Common/PageTitle";
import BaseChart from "../../Components/Analytics/BaseChart";
import SearchableDropdown from "../../Components/Subscription/SearchableDropdown";
import { getDashboardTabs, getDashboardOrganisations, getDashboardAnalytics } from "../../Services/DashboardService";
import { getClientLogo } from "../../Services/ClientService";
import brandLogo from "../../Assets/Images/aegis-icons.png";
import { buildDashboard, groupCharts } from "../../Utils/dashboardChartAdapter";
import "../../Styles/Dashboard.css";

/* ---------------------------------------------------------------- icons -- */

const TAB_ICON = {
  dashboard: MdSpaceDashboard,
  clients: MdGroups,
  providers: MdLocalHospital,
  patients: MdPersonPin,
  locations: MdLocationOn,
  login_credentials: MdKey,
  credentialing: MdVerifiedUser,
  users: MdManageAccounts,
  roles_permissions: MdAdminPanelSettings,
  subscription: MdCreditCard,
  audit_logs: MdFactCheck,
  ai: MdAutoAwesome,
};

const GREETING = {
  morning: { text: "Good morning", icon: MdWbTwilight },
  afternoon: { text: "Good afternoon", icon: MdWbSunny },
  evening: { text: "Good evening", icon: MdDarkMode },
};

const getBrowserGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    return GREETING.morning;
  }
  if (hour >= 12 && hour < 17) {
    return GREETING.afternoon;
  }
  return GREETING.evening;
};

// The AI tab is one tab with two engines behind ?ai_type=.
const AI_MODES = [
  { id: "analytics", label: "Analytics AI", icon: MdQueryStats },
  { id: "coding", label: "Coding AI", icon: MdMemory },
];

/* ----------------------------------------------------- date/format utils -- */

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const relativeTime = (value) => {
  const d = parseDate(value);
  if (!d) return null;
  const secs = Math.round((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
};

const daysUntil = (value) => {
  const d = parseDate(value);
  if (!d) return null;
  const days = Math.ceil((d.getTime() - Date.now()) / 86_400_000);
  if (days < 0) return "expired";
  if (days === 0) return "today";
  if (days < 45) return `${days}d`;
  const months = Math.round(days / 30);
  return `${months}mo`;
};

const shortDate = (value) => {
  const d = parseDate(value);
  if (!d) return null;
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

/* ------------------------------------------------------------- hooks ----- */

function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return reduced;
}

function useInView(disabled) {
  const ref = useRef(null);
  const [inView, setInView] = useState(Boolean(disabled));
  useEffect(() => {
    if (disabled) {
      setInView(true);
      return undefined;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -6% 0px", threshold: 0.06 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [disabled]);
  return [ref, inView];
}

function useCountUp(text, reduced) {
  const [display, setDisplay] = useState(text);
  useEffect(() => {
    const str = String(text);
    if (reduced) {
      setDisplay(str);
      return undefined;
    }
    const match = str.match(/^([^\d-]*)(-?\d[\d,]*(?:\.\d+)?)(.*)$/);
    if (!match) {
      setDisplay(str);
      return undefined;
    }
    const [, pre, numStr, post] = match;
    const hasComma = numStr.includes(",");
    const decimals = numStr.includes(".") ? numStr.split(".")[1].length : 0;
    const target = parseFloat(numStr.replace(/,/g, ""));
    if (!Number.isFinite(target)) {
      setDisplay(str);
      return undefined;
    }
    const fmt = (v) => {
      let s = decimals ? v.toFixed(decimals) : String(Math.round(v));
      if (hasComma) {
        s = Number(s).toLocaleString("en-US", {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
      }
      return `${pre}${s}${post}`;
    };
    let raf = 0;
    let startTs = 0;
    const dur = 900;
    const step = (ts) => {
      if (!startTs) startTs = ts;
      const p = Math.min(1, (ts - startTs) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(fmt(target * eased));
      if (p < 1) raf = requestAnimationFrame(step);
      else setDisplay(fmt(target));
    };
    setDisplay(fmt(0));
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [text, reduced]);
  return display;
}

function CountUpValue({ text, reduced }) {
  return <>{useCountUp(text, reduced)}</>;
}

/* -------------------------------------------------- small components ----- */

function Sparkline({ data, tone, reduced }) {
  const w = 108;
  const h = 34;
  const pad = 3;
  const safe = Array.isArray(data) && data.length > 1 ? data : null;
  if (!safe) return null;
  const min = Math.min(...safe);
  const max = Math.max(...safe);
  const rng = max - min || 1;
  const pts = safe.map((v, i) => [
    pad + (i / (safe.length - 1)) * (w - pad * 2),
    pad + (1 - (v - min) / rng) * (h - pad * 2),
  ]);
  const line = pts
    .map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(" ");
  const last = pts[pts.length - 1];
  const area = `${line} L${last[0].toFixed(1)} ${h} L${pts[0][0].toFixed(1)} ${h} Z`;
  const stroke =
    tone === "bad"
      ? "var(--hc-status-danger-text)"
      : tone === "flat"
      ? "var(--hc-text-muted-slate)"
      : "var(--hc-status-success-text)";
  return (
    <svg
      className="hc-dash-spark"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d={area} style={{ fill: stroke, opacity: 0.12, stroke: "none" }} />
      <path
        className={reduced ? "" : "hc-dash-spark-line"}
        pathLength="1"
        d={line}
        style={{ stroke, fill: "none", strokeWidth: 1.8 }}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        className={reduced ? "" : "hc-dash-spark-dot"}
        cx={last[0].toFixed(1)}
        cy={last[1].toFixed(1)}
        r="2.4"
        style={{ fill: stroke }}
      />
    </svg>
  );
}

function DeltaChip({ kpi }) {
  if (!kpi.delta) return null;
  const flat = kpi.dir === "flat";
  const good = !flat && !kpi.bad;
  const Arrow =
    kpi.dir === "up" ? MdArrowUpward : kpi.dir === "down" ? MdArrowDownward : MdTrendingFlat;
  const fg = flat
    ? "var(--hc-status-inactive-text)"
    : good
    ? "var(--hc-status-success-text)"
    : "var(--hc-status-danger-text)";
  const bg = flat
    ? "var(--hc-status-inactive-bg)"
    : good
    ? "var(--hc-status-success-bg)"
    : "var(--hc-status-danger-bg)";
  return (
    <span className="hc-dash-delta" style={{ color: fg, background: bg }}>
      <Arrow size={12} />
      {kpi.delta}
    </span>
  );
}

function FlipKpiCard({ kpi, Icon, reduced, refreshedAt }) {
  const [flipped, setFlipped] = useState(false);
  const tone = kpi.tone || "blue";
  const sparkTone = kpi.dir === "flat" ? "flat" : kpi.bad ? "bad" : "good";
  const toggle = () => setFlipped((f) => !f);
  return (
    <div className="col-12 col-sm-6 col-xl-3">
      <div
        className={`hc-dash-kpi ${flipped ? "is-flipped" : ""}`}
        data-tone={tone}
        role="button"
        tabIndex={0}
        aria-label={`${kpi.label}: ${kpi.value}. Activate to reveal the definition.`}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <div className="hc-dash-kpi-inner">
          <div className="hc-dash-kpi-face hc-dash-kpi-front">
            <span className="hc-dash-kpi-watermark" aria-hidden="true">
              <Icon size={104} />
            </span>
            <div className="hc-dash-kpi-top">
              <div style={{ minWidth: 0 }}>
                <div className="hc-dash-kpi-label">{kpi.label}</div>
                <div className="hc-dash-kpi-value">
                  <CountUpValue text={kpi.value} reduced={reduced} />
                </div>
                <span className="hc-dash-kpi-rule" />
              </div>
              <div className="hc-dash-kpi-icon">
                <Icon size={20} />
              </div>
            </div>
            <div className="hc-dash-kpi-mid">
              {kpi.submetrics?.length ? (
                <div className="hc-dash-kpi-submetrics">
                  {kpi.submetrics.map((s) => (
                    <span key={s.label} className="hc-dash-kpi-submetric">
                      <span className="k">{s.label}</span>
                      <span className="v">{s.value}</span>
                    </span>
                  ))}
                </div>
              ) : (
                <span className="hc-dash-kpi-desc">{kpi.back?.def || ""}</span>
              )}
            </div>

            {(kpi.delta || kpi.spark) && (
              <div className="hc-dash-kpi-foot">
                {kpi.delta ? <DeltaChip kpi={kpi} /> : <span aria-hidden="true" />}
                <Sparkline data={kpi.spark} tone={sparkTone} reduced={reduced} />
              </div>
            )}

            <span className="hc-dash-kpi-hint">
              <MdArrowForward size={11} />
              Details
            </span>
          </div>

          <div className="hc-dash-kpi-face hc-dash-kpi-back">
            <div className="hc-dash-kpi-back-head">
              <span>{kpi.label}</span>
              <span className="v">{kpi.value}</span>
            </div>
            {kpi.back?.def ? (
              <div className="hc-dash-kpi-def">{kpi.back.def}</div>
            ) : null}
            {kpi.back?.formula ? (
              <div className="hc-dash-kpi-formula">
                <span className="k">How it's calculated</span>
                <span>{kpi.back.formula}</span>
              </div>
            ) : null}
            {kpi.back?.details?.length ? (
              <div className="hc-dash-kpi-detrows">
                {kpi.back.details.map((d) => (
                  <div key={d.label} className="hc-dash-kpi-detrow">
                    <span className="k">{d.label}</span>
                    <span className="v">{d.value}</span>
                  </div>
                ))}
              </div>
            ) : null}
            <div className="hc-dash-kpi-def hc-dash-kpi-fresh">
              Updated {refreshedAt ? relativeTime(refreshedAt) || "just now" : "just now"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- section bar ----- */

export function SectionBar({ tabs, activeId, onChange, perspective }) {
  const trackRef = useRef(null);
  const itemRefs = useRef({});
  const [pill, setPill] = useState({ left: 0, width: 0 });

  const measure = useCallback(() => {
    const el = itemRefs.current[activeId];
    if (el) setPill({ left: el.offsetLeft, width: el.offsetWidth });
  }, [activeId]);

  useLayoutEffect(() => {
    measure();
  }, [measure, tabs, perspective]);

  useEffect(() => {
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    let ro;
    if (typeof ResizeObserver !== "undefined" && trackRef.current) {
      ro = new ResizeObserver(() => measure());
      ro.observe(trackRef.current);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, [measure]);

  const select = (id) => {
    onChange(id);
    const el = itemRefs.current[id];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  };

  const onKeyDown = (e, index) => {
    let next = index;
    if (e.key === "ArrowRight") next = Math.min(tabs.length - 1, index + 1);
    else if (e.key === "ArrowLeft") next = Math.max(0, index - 1);
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    else return;
    e.preventDefault();
    const id = tabs[next].id;
    select(id);
    const el = itemRefs.current[id];
    if (el) el.focus();
  };

  return (
    <div className="hc-dash-segbar" data-perspective={perspective}>
      <div ref={trackRef} className="hc-dash-segbar-track">
        <span
          className="hc-dash-segbar-pill"
          style={{ transform: `translateX(${pill.left}px)`, width: pill.width }}
        />
        {tabs.map((tab, index) => {
          const Icon = tab.icon || TAB_ICON[tab.id] || MdSpaceDashboard;
          const isActive = tab.id === activeId;
          return (
            <button
              key={tab.id}
              type="button"
              ref={(el) => {
                itemRefs.current[tab.id] = el;
              }}
              onClick={() => select(tab.id)}
              onKeyDown={(e) => onKeyDown(e, index)}
              aria-pressed={isActive}
              className={`hc-dash-seg ${isActive ? "is-active" : ""}`}
            >
              <span className="hc-dash-seg-ico">
                <Icon size={16} />
              </span>
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- hero --- */

function HeroTiles({ tiles, reduced }) {
  return (
    <div className="d-flex flex-wrap gap-2 mb-3">
      {tiles.map((f) => (
        <div key={f.label} className="hc-dash-hero-tile px-3 py-2">
          <div className="text-white fw-bold" style={{ fontSize: "1.12rem", lineHeight: 1 }}>
            {f.animate === false ? f.value : <CountUpValue text={f.value} reduced={reduced} />}
          </div>
          <div className="text-white" style={{ fontSize: "0.7rem", opacity: 0.82 }}>
            {f.label}
          </div>
        </div>
      ))}
    </div>
  );
}

function DashboardHero({ perspective, decodedToken, token, reduced, tabCount, filterLabel, selectedOrg, refreshedAt }) {
  const [clientLogoSrc, setClientLogoSrc] = useState(null);

  useEffect(() => {
    if (perspective === "superadmin" && !selectedOrg) {
      setClientLogoSrc(null);
      return;
    }

    const clientId = perspective === "superadmin"
      ? selectedOrg
      : (
          decodedToken?.account_id ||
          decodedToken?.client_id ||
          decodedToken?.ClientId ||
          decodedToken?.organization_id ||
          decodedToken?.OrganizationId ||
          decodedToken?.account?.id ||
          decodedToken?.client?.id ||
          decodedToken?.user_id
        );

    if (!clientId || !token) {
      setClientLogoSrc(null);
      return;
    }

    let isMounted = true;
    getClientLogo(clientId, token)
      .then((res) => {
        if (!isMounted) return;
        if (res?.is_success && res?.data) {
          setClientLogoSrc(res.data);
        } else {
          setClientLogoSrc(null);
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error("Failed to load client logo for dashboard hero:", err);
        setClientLogoSrc(null);
      });

    return () => {
      isMounted = false;
    };
  }, [perspective, selectedOrg, decodedToken, token]);

  const [greet, setGreet] = useState(() => getBrowserGreeting());

  useEffect(() => {
    const syncGreeting = () => setGreet(getBrowserGreeting());
    syncGreeting();
    const interval = setInterval(syncGreeting, 60000); // sync with browser local clock every minute
    return () => clearInterval(interval);
  }, []);

  const GreetIcon = greet.icon;

  const first = (decodedToken?.username || "there").trim().split(/\s+/)[0] || "there";
  const role = decodedToken?.role || (perspective === "superadmin" ? "Platform Operator" : "Administrator");
  const account = decodedToken?.account_name || "Aegis 360";
  const status = decodedToken?.account_status;

  const tiles = useMemo(() => {
    if (perspective === "superadmin") {
      return [
        decodedToken?.total_clients != null && { label: "Client organizations", value: String(decodedToken.total_clients) },
        decodedToken?.active_subscriptions != null && { label: "Active subscriptions", value: String(decodedToken.active_subscriptions) },
        shortDate(decodedToken?.member_since) && { label: "On the platform since", value: shortDate(decodedToken.member_since), animate: false },
        relativeTime(decodedToken?.last_login) && { label: "Your last sign-in", value: relativeTime(decodedToken.last_login), animate: false },
      ].filter(Boolean);
    }
    return [
      decodedToken?.team_size != null && { label: "Active team members", value: String(decodedToken.team_size) },
      decodedToken?.plan_name && { label: "Current plan", value: decodedToken.plan_name, animate: false },
      daysUntil(decodedToken?.renewal_date) && { label: "To renewal", value: daysUntil(decodedToken.renewal_date), animate: false },
      status && { label: "Account status", value: status, animate: false },
    ].filter(Boolean);
  }, [perspective, decodedToken, status]);

  const panelRows = useMemo(() => {
    const rows = [
      { k: "Role", v: role },
      perspective === "superadmin"
        ? { k: "Scope", v: filterLabel || "All client organizations" }
        : { k: "Organization", v: account },
      shortDate(decodedToken?.member_since) && { k: "Member since", v: shortDate(decodedToken.member_since) },
      relativeTime(decodedToken?.last_login) && { k: "Last sign-in", v: relativeTime(decodedToken.last_login) },
    ].filter(Boolean);
    return rows;
  }, [role, perspective, filterLabel, account, decodedToken]);

  const refreshedLabel = refreshedAt ? relativeTime(refreshedAt) || "just now" : "loading…";

  return (
    <div
      className="hc-hero-banner hc-hero-banner-primary hc-dash-hero position-relative overflow-hidden p-4 p-lg-5 mb-3"
      data-perspective={perspective}
    >
      <span aria-hidden="true" className="hc-dash-hero-orb a" />
      <span aria-hidden="true" className="hc-dash-hero-orb b" />

      <div className="row g-4 align-items-stretch position-relative" style={{ zIndex: 1 }}>
        <div className="col-lg-7">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div className="hc-dash-hero-brand-badge">
              {perspective === "superadmin" && !selectedOrg ? (
                <img
                  src={brandLogo}
                  alt="Aegis 360"
                  className="hc-dash-hero-logo-img"
                />
              ) : clientLogoSrc ? (
                <img
                  src={clientLogoSrc}
                  alt={`${account} logo`}
                  className="hc-dash-hero-logo-img"
                />
              ) : (
                <img
                  src={brandLogo}
                  alt={account}
                  className="hc-dash-hero-logo-img"
                />
              )}
            </div>
            <span className="hc-dash-hero-eyebrow d-inline-flex align-items-center gap-1 rounded-pill fw-semibold">
              <MdBolt size={13} />
              {perspective === "superadmin" ? "Platform Command Center" : "Practice Command Center"}
            </span>
          </div>

          <h1 className="hc-dash-greet fw-bold mb-2">
            <span className="hc-dash-greet-icon">
              <GreetIcon size={26} />
            </span>{" "}
            {greet.text}, {first}
          </h1>

          <div className="hc-dash-hero-identity mb-4">
            <span>{role}</span>
            <span className="sep" />
            <span>{account}</span>
            {status ? <span className="hc-dash-hero-role">{status}</span> : null}
          </div>

          {tiles.length ? <HeroTiles tiles={tiles} reduced={reduced} /> : null}

          <div className="hc-dash-attn">
            <span className={`hc-dash-fresh-dot`} />
            <span className="hc-dash-attn-body">
              Live data · refreshed {refreshedLabel}
              {filterLabel ? <> · viewing <strong>{filterLabel}</strong></> : null}
            </span>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="hc-dash-hero-panel p-4 h-100">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="text-white fw-semibold d-inline-flex align-items-center gap-2">
                <MdInsights size={17} />
                Your account
              </span>
              <span
                className="badge rounded-pill fw-semibold"
                style={{
                  background: "var(--hc-status-success-bg)",
                  color: "var(--hc-status-success-text)",
                }}
              >
                Live
              </span>
            </div>

            <div className="hc-dash-hero-rows">
              {panelRows.map((r) => (
                <div key={r.k} className="hc-dash-hero-row">
                  <span className="k">{r.k}</span>
                  <span className="v">{r.v}</span>
                </div>
              ))}
            </div>

            <div
              className="d-flex align-items-center gap-2 text-white mt-3"
              style={{ fontSize: "0.72rem", opacity: 0.82 }}
            >
              <span className="hc-dash-fresh-dot" />
              {tabCount} dashboard{tabCount === 1 ? "" : "s"} available to you
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- toolbar -------- */

const ALL_ORGS = "__all__";

function ClientFilter({ orgs, value, onChange }) {
  const options = useMemo(
    () => [
      { value: ALL_ORGS, label: "All client organizations" },
      ...orgs.map((o) => ({ value: o.id, label: o.name })),
    ],
    [orgs]
  );
  return (
    <div className="hc-dash-clientfilter" title="Filter dashboard by client organization">
      <MdGroups size={15} aria-hidden="true" />
      <SearchableDropdown
        options={options}
        value={value || ALL_ORGS}
        onChange={(v) => onChange(v && v !== ALL_ORGS ? v : null)}
        placeholder="All client organizations"
        className="hc-dash-clientfilter-select"
      />
    </div>
  );
}

function AiModeToggle({ value, onChange }) {
  return (
    <div className="hc-dash-aitoggle" role="group" aria-label="AI engine">
      {AI_MODES.map((m) => {
        const Ico = m.icon;
        return (
          <button
            key={m.id}
            type="button"
            aria-pressed={value === m.id}
            className={value === m.id ? "is-active" : ""}
            onClick={() => onChange(m.id)}
          >
            <Ico size={14} />
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------- chart grid -- */

const COL_CLASS = {
  12: "col-12",
  8: "col-12 col-xl-8",
  6: "col-12 col-lg-6",
  4: "col-12 col-md-6 col-xl-4",
};
const colClass = (span) => COL_CLASS[span] || (Number(span) === 12 ? "col-12" : "col-12 col-lg-6");

function ChartCard({ chart, span, height, reduced, index }) {
  const [ref, inView] = useInView(reduced);
  return (
    <div
      ref={ref}
      className={`${colClass(span)} hc-dash-chart ${inView ? "in" : ""}`}
      style={{ "--i": index % 6 }}
    >
      <BaseChart
        title={chart.title}
        subtitle={chart.subtitle}
        option={chart.option}
        height={`${height}px`}
        info={chart.info}
        noData={!chart.option}
      />
    </div>
  );
}

function ChartSection({ group, reduced, startIndex }) {
  const [ref, inView] = useInView(reduced);
  return (
    <section ref={ref} className={`hc-dash-section ${inView ? "in" : ""}`}>
      <div className="hc-dash-section-head">
        <span className="hc-dash-section-dot" />
        <h3 className="hc-dash-section-title">{group.label}</h3>
      </div>
      <div className="row g-4">
        {group.charts.map((chart, i) => (
          <ChartCard
            key={chart.key || i}
            chart={chart}
            span={group.spans[i]}
            height={group.heights[i]}
            reduced={reduced}
            index={startIndex + i}
          />
        ))}
      </div>
    </section>
  );
}

/* --------------------------------------------------- loading / states --- */

function KpiSkeleton() {
  return (
    <div className="row g-3 mt-1 mb-2">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="col-12 col-sm-6 col-xl-3">
          <div className="hc-dash-skel hc-dash-skel-kpi">
            <span className="hc-dash-skel-line w-50" />
            <span className="hc-dash-skel-line hc-dash-skel-line-lg w-75" />
            <span className="hc-dash-skel-line w-100 mt-auto" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="row g-4 mt-1">
      {[6, 6, 4, 4, 4, 12].map((span, i) => (
        <div key={i} className={colClass(span)}>
          <div
            className="hc-dash-skel hc-dash-skel-chart"
            style={{ height: span === 12 ? 380 : 300 }}
          >
            <span className="hc-dash-skel-line w-25" />
            <span className="hc-dash-skel-chartbody" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="hc-dash-state hc-dash-state-error">
      <MdErrorOutline size={30} />
      <p>{message || "We couldn't load this dashboard."}</p>
      <button type="button" onClick={onRetry}>
        <MdRefresh size={15} /> Try again
      </button>
    </div>
  );
}

/* --------------------------------------------------------------- page --- */

const TAB_PERMISSIONS = {
  clients: [PermissionCodes.CLIENTS_LIST],
  providers: [PermissionCodes.PROVIDERS_LIST],
  patients: [PermissionCodes.PATIENTS_LIST],
  locations: [PermissionCodes.LOCATIONS_LIST],
  login_credentials: [PermissionCodes.LOGIN_CREDS_LIST],
  credentialing: [
    PermissionCodes.CREDENTIALING_TRACKING_LIST,
    PermissionCodes.HEALTHCARE_PAYOR_LIST,
  ],
  users: [PermissionCodes.USERS_LIST],
  roles_permissions: [PermissionCodes.ROLES_LIST],
  subscription: [
    PermissionCodes.SUBSCRIBERS_LIST,
    PermissionCodes.SUBSCRIPTION_PLANS_LIST,
  ],
  audit_logs: [PermissionCodes.AUDIT_LOGS_LIST],
  // The AI tab is the Coding-AI / Analytics-AI telemetry view — gated on those two
  // view permissions only. shapeAiTab() below then splits it: both -> "AI" + engine
  // toggle, one -> that engine's tab with no toggle, neither -> no tab.
  ai: [PermissionCodes.CODING_AI_VIEW, PermissionCodes.ANALYTICS_AI_VIEW],
};

const FALLBACK_TABS = [
  { id: "dashboard", label: "Overview" },
  { id: "clients", label: "Clients" },
  { id: "providers", label: "Providers" },
  { id: "patients", label: "Patients" },
  { id: "locations", label: "Locations" },
  { id: "login_credentials", label: "Login Credentials" },
  { id: "credentialing", label: "Credentialing" },
  { id: "users", label: "Users" },
  { id: "roles_permissions", label: "Roles & Permissions" },
  { id: "subscription", label: "Subscription" },
  { id: "audit_logs", label: "Audit Logs" },
  { id: "ai", label: "AI" },
];

function Dashboard() {
  const { token, permissions, decodedToken } = useAuth();
  const chartTheme = useChartTheme();
  const reduced = useReducedMotion();

  const userPermissionCodes = useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken]
  );
  const canViewDashboard = permissionHas(
    userPermissionCodes,
    PermissionCodes.DASHBOARD_VIEW,
    decodedToken
  );

  const isSuperAdmin = useMemo(() => isSuperAdminUser(decodedToken), [decodedToken]);

  const perspective = isSuperAdmin ? "superadmin" : "admin";

  // Which AI engines this user may see. Drives the AI-tab shape (toggle vs a
  // single-engine tab) — see shapeAiTab().
  const canCodingAI = useMemo(
    () => isSuperAdmin || userPermissionCodes.includes(PermissionCodes.CODING_AI_VIEW),
    [isSuperAdmin, userPermissionCodes]
  );
  const canAnalyticsAI = useMemo(
    () => isSuperAdmin || userPermissionCodes.includes(PermissionCodes.ANALYTICS_AI_VIEW),
    [isSuperAdmin, userPermissionCodes]
  );

  /* -- tabs (RBAC-filtered server-side; filtered again client-side to match
        the sidebar exactly and stay instant on token change) --------------- */
  const [tabs, setTabs] = useState([]);
  const clientFilterTabs = useCallback(
    (list) =>
      list.filter((tab) => {
        if (tab.id === "dashboard") return true;
        if (isSuperAdmin) return true;
        const required = TAB_PERMISSIONS[tab.id];
        if (!required || !required.length) return true;
        return required.some((perm) => userPermissionCodes.includes(perm));
      }),
    [isSuperAdmin, userPermissionCodes]
  );

  // Split the single "ai" tab by permission:
  //   both engines  -> one "AI" tab with the engine toggle (unchanged behaviour)
  //   coding only   -> "Coding AI" tab, no toggle, always ai_type=coding
  //   analytics only-> "Analytics AI" tab, no toggle, always ai_type=analytics
  //   neither       -> no AI tab
  const shapeAiTab = useCallback(
    (list) =>
      list.flatMap((tab) => {
        if (tab.id !== "ai") return [tab];
        if (canCodingAI && canAnalyticsAI) return [{ ...tab, label: "AI" }];
        if (canCodingAI)
          return [{ ...tab, label: "Coding AI", aiOnly: "coding", icon: MdMemory }];
        if (canAnalyticsAI)
          return [{ ...tab, label: "Analytics AI", aiOnly: "analytics", icon: MdQueryStats }];
        return [];
      }),
    [canCodingAI, canAnalyticsAI]
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    getDashboardTabs(token)
      .then((res) => {
        if (cancelled) return;
        const list =
          res?.is_success && Array.isArray(res.data) && res.data.length
            ? res.data
            : FALLBACK_TABS;
        setTabs(shapeAiTab(clientFilterTabs(list)));
      })
      .catch(() => {
        if (!cancelled) setTabs(shapeAiTab(clientFilterTabs(FALLBACK_TABS)));
      });
    return () => {
      cancelled = true;
    };
  }, [token, clientFilterTabs, shapeAiTab]);

  /* -- superadmin client filter ------------------------------------------- */
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  useEffect(() => {
    if (!token || !isSuperAdmin) return;
    let cancelled = false;
    getDashboardOrganisations(token)
      .then((res) => {
        if (!cancelled && res?.is_success && Array.isArray(res.data)) setOrgs(res.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token, isSuperAdmin]);

  /* -- active tab + AI engine -------------------------------------------- */
  const [activeId, setActiveId] = useState("dashboard");
  const [aiType, setAiType] = useState("analytics");

  useEffect(() => {
    if (!tabs.length) return;
    if (!tabs.some((t) => t.id === activeId)) setActiveId(tabs[0].id);
  }, [tabs, activeId]);

  const activeTab = tabs.find((t) => t.id === activeId) || { id: activeId, label: "Overview" };
  // For a single-engine AI tab the engine is fixed; otherwise the toggle decides.
  const aiOnly = activeTab.aiOnly || null;
  const effectiveAiType = aiOnly || aiType;
  const ActiveIcon = activeTab.icon || TAB_ICON[activeId] || MdSpaceDashboard;

  /* -- analytics fetch --------------------------------------------------- */
  const [raw, setRaw] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | refreshing | ready | error
  const [errMsg, setErrMsg] = useState("");
  const [refreshedAt, setRefreshedAt] = useState(null);
  const reqId = useRef(0);

  const overviewModules = useMemo(
    () => tabs.filter((t) => t.id !== "dashboard").map((t) => t.id),
    [tabs]
  );
  const overviewAiEngines = useMemo(() => {
    const aiTab = tabs.find((t) => t.id === "ai");
    if (!aiTab) return [];
    if (aiTab.aiOnly === "coding") return ["coding"];
    if (aiTab.aiOnly === "analytics") return ["analytics"];
    return ["analytics", "coding"];
  }, [tabs]);

  const load = useCallback(() => {
    if (!token || !activeId) return;
    if (activeId === "dashboard" && !tabs.length) return;
    const rid = ++reqId.current;
    setStatus((s) => (s === "ready" ? "refreshing" : "loading"));
    setErrMsg("");
    getDashboardAnalytics(token, {
      tab: activeId,
      isSuperAdmin,
      accountId: selectedOrg,
      aiType: activeId === "ai" ? effectiveAiType : null,
      modules: activeId === "dashboard" ? overviewModules : null,
      aiEngines: activeId === "dashboard" ? overviewAiEngines : null,
    })
      .then((res) => {
        if (reqId.current !== rid) return;
        if (res?.is_success && res.data && typeof res.data === "object" && !Array.isArray(res.data)) {
          setRaw(res.data);
          setRefreshedAt(Date.now());
          setStatus("ready");
        } else {
          setStatus("error");
          setErrMsg(res?.message || "This dashboard is not available.");
        }
      })
      .catch((err) => {
        if (reqId.current !== rid) return;
        setStatus("error");
        setErrMsg(err?.message || "Network error while loading the dashboard.");
      });
  }, [token, activeId, isSuperAdmin, selectedOrg, effectiveAiType, tabs.length, overviewModules, overviewAiEngines]);

  useEffect(() => {
    load();
  }, [load]);

  const tabKey = activeId === "ai" ? `ai_${effectiveAiType}` : activeId;
  // chartTheme isn't read here: it only invalidates the chart options inside the payload.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const payload = useMemo(() => buildDashboard(tabKey, raw), [tabKey, raw, chartTheme]);

  const filterLabel = useMemo(() => {
    if (!isSuperAdmin || !selectedOrg) return null;
    return orgs.find((o) => o.id === selectedOrg)?.name || null;
  }, [isSuperAdmin, selectedOrg, orgs]);

  const groups = useMemo(() => groupCharts(payload.charts), [payload.charts]);

  if (!canViewDashboard) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">
          You do not have permission to view the dashboard.
        </div>
      </div>
    );
  }

  // A tab switch, a filter change, the AI-engine toggle and a manual refresh all
  // route through load() and land here as "loading" or "refreshing" — every one
  // shows the shimmer skeletons.
  const busy = status === "loading" || status === "refreshing";

  return (
    <>
      <PageTitle title="Dashboard | Aegis 360" />

      <div className="container-fluid py-4">
        <DashboardHero
          perspective={perspective}
          decodedToken={decodedToken}
          token={token}
          reduced={reduced}
          tabCount={tabs.length}
          filterLabel={filterLabel}
          selectedOrg={selectedOrg}
          refreshedAt={refreshedAt}
        />

        <div className="hc-dash-segwrap">
          <SectionBar
            tabs={tabs}
            activeId={activeId}
            onChange={setActiveId}
            perspective={perspective}
          />
        </div>

        <div key={`${perspective}-${activeId}-${effectiveAiType}-${selectedOrg || "all"}`} className="hc-dash-fade">
          <div className="hc-dash-tabhead">
            <span className="hc-dash-tabhead-ico">
              <ActiveIcon size={20} />
            </span>
            <h2>{activeTab.label}</h2>
            <div className="hc-dash-toolbar">
              {activeId === "ai" && !aiOnly ? (
                <AiModeToggle value={aiType} onChange={setAiType} />
              ) : null}
              {isSuperAdmin ? (
                <ClientFilter orgs={orgs} value={selectedOrg} onChange={setSelectedOrg} />
              ) : null}
              <button
                type="button"
                className="hc-dash-refresh"
                onClick={load}
                disabled={busy}
                aria-label="Refresh"
              >
                <MdRefresh size={16} className={busy ? "spin" : ""} />
              </button>
            </div>
          </div>

          {busy ? (
            <>
              <KpiSkeleton />
              <ChartSkeleton />
            </>
          ) : status === "error" ? (
            <ErrorState message={errMsg} onRetry={load} />
          ) : (
            <>
              <div className="row g-3 mt-1 mb-2">
                {payload.kpis.map((kpi) => (
                  <FlipKpiCard
                    key={kpi.label}
                    kpi={kpi}
                    Icon={(kpi.iconKey && TAB_ICON[kpi.iconKey]) || ActiveIcon}
                    reduced={reduced}
                    refreshedAt={refreshedAt}
                  />
                ))}
              </div>

              <div style={{ position: "relative" }}>
                {payload.charts.length === 0 ? (
                  activeId === "dashboard" && payload.kpis.length ? null : (
                    <div className="hc-dash-empty">No analytics data for this view yet.</div>
                  )
                ) : (
                  groups.map((group, gi) => (
                    <ChartSection
                      key={group.label}
                      group={group}
                      reduced={reduced}
                      startIndex={gi * 3}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default Dashboard;
